/**
 * Reusable tree-with-checkboxes picker for selecting parts of a project's
 * doc tree. Output is the same ``AccessRule[]`` shape used by
 * doc_access_rules and now by project_shares too.
 *
 * Used from:
 *   - ExternalUserDetail's LocalProjectModal (per-user doc access config)
 *   - ShareLinksSection's create-share dialog
 */
import { useEffect, useState } from "react";
import { Loader2, ChevronDown, ChevronRight, Check, Minus, Folder, FileText, Code, LifeBuoy, HelpCircle } from "lucide-react";
import { authFetch } from "../utils/authFetch";
import type { AccessRule } from "../hooks/useDocAccess";

interface DocSection { id: string; slug: string; title: string; sectionType: string; }
export interface TreeNode { id: string; title: string; slug: string; slugPath: string; children?: TreeNode[]; }

type CheckState = "checked" | "partial" | "unchecked";

/* ── Pure tree helpers (keyed by slugPath) ─────────────────────────── */

function collectDescendantSlugPaths(node: TreeNode): string[] {
  const paths: string[] = [];
  for (const c of node.children ?? []) { paths.push(c.slugPath); paths.push(...collectDescendantSlugPaths(c)); }
  return paths;
}

function findNodeBySlugPath(nodes: TreeNode[], slugPath: string): TreeNode | null {
  for (const n of nodes) {
    if (n.slugPath === slugPath) return n;
    if (n.children?.length) {
      const found = findNodeBySlugPath(n.children, slugPath);
      if (found) return found;
    }
  }
  return null;
}

function buildRuleMap(rules: AccessRule[]): Map<string, AccessRule> {
  const m = new Map<string, AccessRule>();
  for (const r of rules) m.set(r.slug, r);
  return m;
}

function buildParentMap(nodes: TreeNode[], parentSlugPath: string | null, out: Map<string, string | null>) {
  for (const n of nodes) {
    out.set(n.slugPath, parentSlugPath);
    if (n.children?.length) buildParentMap(n.children, n.slugPath, out);
  }
}

function isAncestorCovered(slugPath: string, parentMap: Map<string, string | null>, ruleMap: Map<string, AccessRule>): boolean {
  let pid = parentMap.get(slugPath) ?? null;
  while (pid) {
    const rule = ruleMap.get(pid);
    if (rule?.includeChildren) return true;
    pid = parentMap.get(pid) ?? null;
  }
  return false;
}

function getNodeCheckState(node: TreeNode, ruleMap: Map<string, AccessRule>, parentMap: Map<string, string | null>): CheckState {
  const rule = ruleMap.get(node.slugPath);
  if (rule?.includeChildren) return "checked";
  if (isAncestorCovered(node.slugPath, parentMap, ruleMap)) return "checked";
  if (rule) {
    if (!node.children?.length) return "checked";
    const allDesc = collectDescendantSlugPaths(node);
    const allChecked = allDesc.every(sp => ruleMap.has(sp) || isAncestorCovered(sp, parentMap, ruleMap));
    return allChecked ? "checked" : "partial";
  }
  if (node.children?.length) {
    const anyChild = node.children.some(c => getNodeCheckState(c, ruleMap, parentMap) !== "unchecked");
    return anyChild ? "partial" : "unchecked";
  }
  return "unchecked";
}

function computeUncheck(slugPath: string, rules: AccessRule[], parentMap: Map<string, string | null>, allTrees: TreeNode[]): AccessRule[] {
  const ruleMap = buildRuleMap(rules);
  const nodeInTree = findNodeBySlugPath(allTrees, slugPath);
  const descPaths = nodeInTree ? new Set(collectDescendantSlugPaths(nodeInTree)) : new Set<string>();
  let next = rules.filter(r => r.slug !== slugPath && !descPaths.has(r.slug));

  let ancestorPath: string | null = null;
  let pid = parentMap.get(slugPath) ?? null;
  while (pid) {
    if (ruleMap.get(pid)?.includeChildren) { ancestorPath = pid; break; }
    pid = parentMap.get(pid) ?? null;
  }

  if (!ancestorPath) return next;

  next = next.filter(r => r.slug !== ancestorPath);

  let currentPath = ancestorPath;
  while (currentPath !== slugPath) {
    const currentNode = findNodeBySlugPath(allTrees, currentPath);
    if (!currentNode?.children?.length) break;

    let pathChildPath: string | null = null;
    for (const child of currentNode.children) {
      if (child.slugPath === slugPath || findNodeBySlugPath(child.children ?? [], slugPath)) {
        pathChildPath = child.slugPath;
        break;
      }
    }
    if (!pathChildPath) break;

    for (const child of currentNode.children) {
      if (child.slugPath !== pathChildPath && !next.some(r => r.slug === child.slugPath)) {
        next.push({ slug: child.slugPath, includeChildren: true });
      }
    }
    if (!next.some(r => r.slug === currentPath)) {
      next.push({ slug: currentPath, includeChildren: false });
    }
    currentPath = pathChildPath;
  }

  return next;
}

/* ── Public component ──────────────────────────────────────────────── */

interface Props {
  projectSlug: string;
  rules: AccessRule[];
  onChange: (rules: AccessRule[]) => void;
  /** Also list the FAQ and support sections the docs reader hides. Service
   *  accounts (API Access) read those through the KB API, so theirs are
   *  grantable; every other picker leaves them out. */
  includeSupport?: boolean;
}

export default function TreeAccessPicker({ projectSlug, rules, onChange, includeSupport = false }: Props) {
  const [sections, setSections] = useState<DocSection[]>([]);
  const [docTrees, setDocTrees] = useState<Record<string, TreeNode[]>>({});
  const [loading, setLoading] = useState(true);
  const [internalSlugs, setInternalSlugs] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const q = includeSupport ? "?includeSupport=1" : "";
      const secRes = await authFetch(`/api/docs/${projectSlug}/sections${q}`);
      if (!secRes.ok) { if (!cancelled) setLoading(false); return; }
      const secs: DocSection[] = await secRes.json();
      const treeRes = await authFetch(`/api/docs/${projectSlug}/tree${q}`);
      const fullTree = treeRes.ok ? ((await treeRes.json()).tree || []) : [];
      if (!cancelled) setInternalSlugs(new Set(fullTree.filter((r: { visibility?: string }) => r.visibility === "internal").map((r: { slug: string }) => r.slug)));
      const trees: Record<string, TreeNode[]> = {};
      for (const s of secs) {
        const sectionNode = fullTree.find((n: any) => n.slug === s.slug);
        trees[s.id] = sectionNode?.children || [];
      }
      if (!cancelled) {
        setSections(secs);
        setDocTrees(trees);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectSlug, includeSupport]);

  const parentMap = new Map<string, string | null>();
  for (const sec of sections) {
    parentMap.set(sec.slug, null);
    buildParentMap(docTrees[sec.id] ?? [], sec.slug, parentMap);
  }
  const ruleMap = buildRuleMap(rules);

  const allTrees: TreeNode[] = [];
  for (const sec of sections) {
    allTrees.push({ id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] });
  }

  const toggleSection = (sec: DocSection) => {
    const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
    const state = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
    if (state === "checked") {
      onChange(computeUncheck(sec.slug, rules, parentMap, allTrees));
    } else {
      const descPaths = new Set([sec.slug, ...collectDescendantSlugPaths(sectionAsNode)]);
      const next = rules.filter(r => !descPaths.has(r.slug));
      next.push({ slug: sec.slug, includeChildren: true });
      onChange(next);
    }
  };

  const toggleNode = (slugPath: string, node: TreeNode) => {
    const state = getNodeCheckState(node, ruleMap, parentMap);
    if (state === "checked") {
      onChange(computeUncheck(slugPath, rules, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectDescendantSlugPaths(node));
      const next = rules.filter(r => !descPaths.has(r.slug) && r.slug !== slugPath);
      next.push({ slug: slugPath, includeChildren: true });
      onChange(next);
    }
  };

  if (loading) {
    return <div className="py-6 flex items-center justify-center gap-1.5 text-xs text-slate-400"><Loader2 className="size-3 animate-spin" /> Loading…</div>;
  }
  if (sections.length === 0) {
    return <div className="py-6 text-center text-xs text-slate-400">No content found for this project.</div>;
  }

  return (
    <div className="space-y-3">
      {sections.map(sec => {
        const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
        const secState = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
        // A tick here would do nothing: internal content never reaches outside readers.
        if (internalSlugs.has(sec.slug)) {
          return (
            <div key={sec.id} className="flex items-center gap-2 mb-1 pl-6">
              <FileText className="size-3.5 text-slate-400" />
              <span className="text-[12px] font-semibold text-slate-500">{sec.title}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Internal only, can't be shared</span>
            </div>
          );
        }
        return (
          <div key={sec.id}>
            <div className="flex items-center gap-2 mb-1">
              <Checkbox state={secState} onChange={() => toggleSection(sec)} />
              {sec.sectionType === "api"
                ? <Code className="size-3.5 text-indigo-500" />
                : sec.sectionType === "support"
                  ? <LifeBuoy className="size-3.5 text-sky-500" />
                  : sec.sectionType === "faq"
                    ? <HelpCircle className="size-3.5 text-violet-500" />
                    : <FileText className="size-3.5 text-slate-500" />}
              <span className="text-[12px] font-semibold text-slate-700">{sec.title}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">{sec.sectionType}</span>
            </div>
            {docTrees[sec.id]?.map(n => (
              <NodeRow key={n.id} node={n} depth={1} ruleMap={ruleMap} parentMap={parentMap} toggleNode={toggleNode} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function Checkbox({ state, onChange }: { state: CheckState; onChange: () => void }) {
  return (
    <button onClick={onChange}
      className={`size-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
        state === "checked" || state === "partial"
          ? "bg-indigo-600 border-indigo-600 text-white"
          : "border-slate-300 hover:border-slate-400"
      }`}>
      {state === "checked" && <Check className="size-2.5" />}
      {state === "partial" && <Minus className="size-2.5" />}
    </button>
  );
}

function NodeRow({ node, depth, ruleMap, parentMap, toggleNode }: {
  node: TreeNode; depth: number;
  ruleMap: Map<string, AccessRule>;
  parentMap: Map<string, string | null>;
  toggleNode: (slugPath: string, node: TreeNode) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = (node.children?.length ?? 0) > 0;
  const state = getNodeCheckState(node, ruleMap, parentMap);

  return (
    <div>
      <div className="flex items-center gap-1.5 py-0.5 rounded hover:bg-slate-50"
        style={{ paddingLeft: depth * 14 }}>
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5 text-slate-400 hover:text-slate-600">
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : <span className="w-4" />}
        <Checkbox state={state} onChange={() => toggleNode(node.slugPath, node)} />
        {hasChildren ? <Folder className="size-3 text-amber-500" /> : <FileText className="size-3 text-slate-400" />}
        <span className="text-[12px] truncate text-slate-700">{node.title}</span>
      </div>
      {expanded && hasChildren && node.children!.map(c => (
        <NodeRow key={c.id} node={c} depth={depth + 1}
          ruleMap={ruleMap} parentMap={parentMap} toggleNode={toggleNode} />
      ))}
    </div>
  );
}
